import { translate } from '@jsverse/transloco';

import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { Comment, Comparison, Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { Report } from 'app/providers/storage-api/report.service';
import { StringBuilder } from 'app/utils/string-builder';
import { Utils } from 'app/utils/utils';
import {
  AbstractChartReport,
  CommentDataChange,
} from '../chart-report-abstract/chart-report-abstract';
import { ChartDataService } from '../../chart-data.service';
import { HighchartsOptions } from '../../chart-display.service';

interface DonutItems {
  metric: { [key: string]: any };
  donutBox?: { [key: string]: any };
  donutRating?: { [key: string]: any };
}

export class DonutChartReport extends AbstractChartReport {
  public reportName = 'documentNumericMetadata';
  public reportHandler = (dashboard, widget, response, options) =>
    ChartDataService.forDonut(
      dashboard,
      widget,
      response,
      options,
      this.comparisonService,
      this.options
    );
  public canExportAsPptx = false;

  public getFields(): string[] {
    return [];
  }

  public canSort(): boolean {
    return this.options.donutOptions.scoring === 'rating';
  }

  public canHideItems(): boolean {
    return false;
  }

  public canDrilldown(): boolean {
    return false;
  }

  getCommentsForSeries(
    _series: any,
    widgetPayload: WidgetChartOptions,
    _comparison?: Comparison
  ): Comment[] {
    return [...(widgetPayload.comments || [])];
  }

  protected getReportSpecificCommentDataChange(
    comment: Comment,
    widget: Widget<WidgetChartOptions>,
    _dashboard: Dashboard,
    currentData: any,
    dataProp: string
  ): CommentDataChange {
    const changeReasons = [];
    if (comment.payload.donutOptions.scoring !== widget.payload.donutOptions.scoring) {
      changeReasons.push('SCORING_STRATEGY_CHANGED');
    }

    if (
      /* eslint-disable eqeqeq */
      comment.payload.donutOptions.bottomBoxSize != widget.payload.donutOptions.bottomBoxSize ||
      comment.payload.donutOptions.topBoxSize != widget.payload.donutOptions.topBoxSize
      /* eslint-enable eqeqeq */
    ) {
      changeReasons.push('BOX_SIZE_CHANGED');
    }

    if (comment.payload.donutOptions.metric !== widget.payload.donutOptions.metric) {
      changeReasons.push('RATING_FIELD_CHANGED');
    }

    if (
      /* eslint-disable eqeqeq */
      comment.payload.donutOptions.minRating != widget.payload.donutOptions.minRating ||
      comment.payload.donutOptions.maxRating != widget.payload.donutOptions.maxRating
      /* eslint-enable eqeqeq */
    ) {
      changeReasons.push('RATING_SCALE_CHANGED');
    }

    if (changeReasons.length) {
      return {
        [dataProp]: currentData?.[dataProp],
        reasons: changeReasons,
      };
    }
  }

  public async executeReport(
    format: 'json' | 'csv',
    passedQuery: string
  ): Promise<string | Report<{ [key: string]: any }>> {
    const params = await super.getParams();

    const body = this.getBody();

    // if donutScoring equals 'body', it'll contain inner number and donut ring
    const numericMetadataResponse = (await this.reportService.postReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.reportName,
      params,
      body,
      'json',
      passedQuery,
      undefined,
      undefined,
      this.options.projectId
    )) as Report<{ [key: string]: any }>;

    this.lastDocumentCount = numericMetadataResponse.totalHits;

    const items: DonutItems = {
      metric: numericMetadataResponse.items.data.metadata[0],
    };

    // for rating donut ring we need another call
    if (this.options.donutOptions.scoring === 'rating') {
      const metadataTopFeaturesResponse = await this.reportService.getReport(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        'metadataTopFeatures',
        params,
        'json',
        passedQuery,
        this.filterService.buildFilterStringClearPolarityFilters(
          this.options.filters,
          this.options.analyticItem
        ),
        undefined,
        this.options.projectId
      );

      items.donutRating = (
        metadataTopFeaturesResponse as Report<{ [key: string]: any }>
      ).items.count;
    } else {
      items.donutBox = numericMetadataResponse.items.data.metadata[1];
    }

    const response = {
      ...numericMetadataResponse,
      items: {
        ...items,
      },
    };

    if (format === 'csv') {
      return this.formatToCsv(items);
    }

    return response;
  }

  private getBody() {
    const body = [];

    const donutOptions = this.options.donutOptions;

    const metricBody = {
      customField: this.options.analyticItem,
    };

    switch (donutOptions.metric) {
      case 'avg':
        metricBody['scoringStrategy'] = 'AVERAGE';
        break;
      case 'sum':
        metricBody['scoringStrategy'] = 'SUM';
        break;
      case 'box':
        {
          metricBody['scoringStrategy'] = 'BOX';
          metricBody['scoreRange'] = `${donutOptions.minRating}:${donutOptions.maxRating}`;
          metricBody['boxDirection'] = donutOptions.metricBoxType.toLocaleUpperCase();
          metricBody['boxSize'] = donutOptions.metricBoxSize;
        }
        break;
      case 'nps':
        metricBody['scoringStrategy'] = 'NPS';
        metricBody['scoreRange'] = `${donutOptions.minRating}:${donutOptions.maxRating}`;
        break;
    }

    body.push(metricBody);

    if (donutOptions.scoring === 'box') {
      const donutBody = {
        customField: this.options.analyticItem,
        scoringStrategy: 'BOX',
        scoreRange: `${donutOptions.minRating}:${donutOptions.maxRating}`,
      };

      if (donutOptions.isTopShown) {
        donutBody['boxTopSize'] = donutOptions.topBoxSize;
      }

      if (donutOptions.isBottomShown) {
        donutBody['boxBottomSize'] = donutOptions.bottomBoxSize;
      }

      body.push(donutBody);
    }

    return body;
  }

  private formatToCsv(items: DonutItems): string {
    const sb = new StringBuilder();

    sb.appendLine('metric,element,score,method');

    const tableRows = DonutChartReportUtils.toTable(items, this.options);
    tableRows.forEach((r) => sb.appendLine(`${r.metric},${r.element},${r.score},${r.method}`));

    return sb.toString();
  }

  public getHighchartsOptions(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ): HighchartsOptions {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    ret.tooltip.headerFormat = `${translate('widgets.charting.tooltip.name-part', {
      name: "'{point.key'}",
    })}<br/>`;

    if (widget.payload.donutOptions.scoring === 'box') {
      ret.tooltip.pointFormatter = function () {
        return `${translate('widgets.charting.tooltip.score-part', {
          score: Utils.addThousandSeparator(Utils.round(this.y, this.series.options.maxDecimals)),
        })}`;
      };
    } else {
      ret.tooltip.pointFormatter = function () {
        return `${translate('widgets.charting.tooltip.count-total-percentage-part', {
          count: Utils.addThousandSeparator(Utils.round(this.y, this.series.options.maxDecimals)),
          total: Utils.addThousandSeparator(this.series.options.custom.documentCount),
          percentage: Utils.roundNearest((this.y * 100) / this.series.options.custom.documentCount),
        })}`;
      };
    }

    return ret;
  }
}

interface DonutTableRow {
  metric: string;
  element: string;
  score: number;
  method: string;
}

export class DonutChartReportUtils {
  public static toTable(items: DonutItems, payload: WidgetChartOptions): DonutTableRow[] {
    const result: DonutTableRow[] = [];

    const donutOptions = payload.donutOptions;

    const metric = FieldService.getFieldLongName(payload.analyticItem);

    let innerScore: number;
    const metricData = items.metric;
    if (donutOptions.metric === 'box') {
      if (donutOptions.metricBoxType === 'top') {
        innerScore = metricData.topScore;
      } else {
        innerScore = metricData.bottomScore;
      }
    } else {
      innerScore = metricData.score;
    }

    let innerMethod: string;
    switch (donutOptions.metric) {
      case 'avg':
        innerMethod = 'Average';
        break;
      case 'sum':
        innerMethod = 'Sum';
        break;
      case 'box':
        innerMethod = donutOptions.metricBoxType === 'top' ? 'TopBox' : 'BottomBox';
        break;
      case 'nps':
        innerMethod = 'NPS';
        break;
    }

    result.push({
      metric,
      element: 'Inner',
      score: innerScore,
      method: innerMethod,
    });

    if (donutOptions.scoring === 'box') {
      const donutBox = items.donutBox;

      const addRow = (score: number, method: string) => {
        result.push({
          metric,
          element: 'Donut',
          score,
          method,
        });
      };

      if (donutOptions.isTopShown) {
        addRow(donutBox.topScore, 'TopBox');
      }

      if (donutOptions.isNeutralShown) {
        addRow(donutBox.neutralScore, 'Neutral');
      }

      if (donutOptions.isBottomShown) {
        addRow(donutBox.bottomScore, 'BottomBox');
      }
    } else {
      const donutRating = items.donutRating;

      Object.getOwnPropertyNames(donutRating).forEach((key) => {
        result.push({
          metric,
          element: 'Donut',
          score: donutRating[key],
          method: 'Rating',
        });
      });
    }

    return result;
  }
}
